import { useCallback, useEffect } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { CircularProgress, TextField, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import distributionApi from 'services/api/distribution';
import { useTextFieldValidation } from 'pages/Distribution/hooks';
import useValidationSchema from './useValidationSchema';

type PropsTypes = {
  loading: boolean;
  error: SerializedError | null;
  onValidationStarted: (artistName: string | null) => void;
  onValidationEnded: (validationError: SerializedError | null) => void;
};

const ArtistName = ({
  loading,
  error,
  onValidationStarted,
  onValidationEnded,
}: PropsTypes) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [
    setupArtistName,
    {
      data,
      isSuccess: backendValidationSuccess,
      error: backendValidationError,
      isLoading: backendValidationLoading,
    },
  ] = distributionApi.useSetupArtistNameMutation();

  useEffect(() => {
    if (!backendValidationError) {
      return;
    }
    onValidationEnded(backendValidationError as SerializedError);
  }, [backendValidationError, onValidationEnded]);

  useEffect(() => {
    if (!backendValidationSuccess) {
      return;
    }
    onValidationEnded(null);
  }, [backendValidationSuccess, onValidationEnded]);

  const handleFrontendValidationSuccess = useCallback(
    (value: string) => {
      if (data?.name === value) {
        return;
      }
      onValidationStarted(value);
      setupArtistName({ name: value });
    },
    [data, setupArtistName, onValidationStarted]
  );

  const handleFrontendValidationError = useCallback(
    (err: SerializedError | null) => {
      onValidationEnded(err);
    },
    [onValidationEnded]
  );

  const validationSchema = useValidationSchema();
  const { onChange, onBlur } = useTextFieldValidation({
    validator: validationSchema,
    onValidationSuccess: handleFrontendValidationSuccess,
    error,
    onValidationError: handleFrontendValidationError,
  });

  return (
    <TextField
      fullWidth
      disabled={loading}
      label={t('distribution:artistName:placeholder')}
      defaultValue={data}
      error={!!error}
      helperText={error && error.message}
      InputProps={{
        ...(backendValidationLoading && {
          endAdornment: <CircularProgress size={theme.spacing(3)} />,
        }),
      }}
      onChange={onChange}
      onBlur={onBlur}
    />
  );
};

export default ArtistName;
